import { V2Question, V2Response } from "./helper";

/**
 * Week 0 details form (SURVEY + settings.isWeekZeroDetails): the office's Google Maps link is asked only for an
 * Offline / Hybrid internship. Questions are found by their `metadata.weekZeroField` marker, never by label.
 * Forms without these markers are untouched by everything here.
 */

export const WEEK_ZERO_MODE_MARKER = "internshipMode";
export const WEEK_ZERO_OFFICE_URL_MARKER = "officeLocationUrl";
export const WEEK_ZERO_OFFER_LETTER_MARKER = "offerLetter";
export const WEEK_ZERO_MENTOR_EMAIL_MARKER = "externalMentorEmail";
/** core-api's EXTERNAL_MENTOR_EMAIL_MAX: a longer address is never copied to the internship record. */
export const EXTERNAL_MENTOR_EMAIL_MAX = 254;
export const MENTOR_EMAIL_MESSAGE = "Enter a valid email address, like name@company.com";
export const MENTOR_EMAIL_REQUIRED_MESSAGE = "Enter the external mentor's email";
export const MENTOR_PERSONAL_EMAIL_MESSAGE = "Use the mentor's company email, not a personal one (gmail, yahoo, outlook …)";
/** Personal mail providers refused for the external mentor email. Same list as core-api. */
export const PERSONAL_EMAIL_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.in",
  "yahoo.in",
  "ymail.com",
  "rocketmail.com",
  "outlook.com",
  "outlook.in",
  "hotmail.com",
  "hotmail.co.in",
  "live.com",
  "live.in",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "rediffmail.com",
  "protonmail.com",
  "proton.me",
  "gmx.com",
  "gmx.net",
  "mail.com",
  "yandex.com",
  "yandex.ru",
  "zohomail.in"
];
const PERSONAL_EMAIL_DOMAIN_PATTERN = /^(yahoo|outlook|hotmail|live|gmx|yandex)\.[a-z.]+$/;

/** Whether the email's domain (after the last "@", trimmed, lower-cased) is a personal provider. No "@" means false. */
export const isPersonalEmailDomain = (email: unknown): boolean => {
  const text = typeof email === "string" ? email.trim().toLowerCase() : "";
  const at = text.lastIndexOf("@");
  if (at < 0) return false;
  const domain = text.slice(at + 1);
  return PERSONAL_EMAIL_DOMAINS.includes(domain) || PERSONAL_EMAIL_DOMAIN_PATTERN.test(domain);
};

/** The reason the external mentor email answer is refused, or undefined when it is fine (blank is fine here: required is a separate rule). */
export const mentorEmailError = (value: unknown): string | undefined => {
  if (!isMentorEmailInput(value)) return MENTOR_EMAIL_MESSAGE;
  return isPersonalEmailDomain(value) ? MENTOR_PERSONAL_EMAIL_MESSAGE : undefined;
};
export const WEEK_ZERO_MENTOR_PHONE_MARKER = "externalMentorMobile";
export const MENTOR_PHONE_MESSAGE = "Enter the phone number in digits only, like 919876543210";
export const GOOGLE_MAPS_URL_MESSAGE = "Paste a Google Maps link or the office's coordinates (e.g. 19.1136, 72.8697)";
/** Fixed label description for the office question; the admin-written description is not shown (see week-zero-details.md). */
export const OFFICE_LOCATION_DESCRIPTION =
  "Paste the Google Maps link of your office building. We use it to check you're at the office when you mark attendance.";
export const OFFICE_NOT_FOUND_MESSAGE = "We couldn't find the exact spot in this link. Paste the coordinates instead.";
/** "Use my current location" fills the field with a fix; a fix less accurate than this (in meters) only shows a warning. */
export const CURRENT_LOCATION_MAX_ACCURACY_METERS = 100;

const MAX_URL_LENGTH = 2048;
const TLD = "[a-z]{2,3}(\\.[a-z]{2})?";
const MAPS_GOOGLE_HOST = new RegExp(`^maps\\.google\\.${TLD}$`);
const GOOGLE_HOST = new RegExp(`^(www\\.)?google\\.${TLD}$`);

/** A Google Maps https link (Share link, maps.google.* with a /maps path). Same rule as core-api. */
export const isGoogleMapsUrl = (value: unknown): boolean => {
  if (typeof value !== "string") return false;
  const text = value.trim();
  if (!text || text.length > MAX_URL_LENGTH) return false;

  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return false;
  }
  if (url.protocol !== "https:" || url.username || url.password) return false;

  const host = url.hostname.toLowerCase();
  // "/maps" exactly or "/maps/...": google.com/mapsfoo is not a map. Same as core-api, which lowercases the path.
  const path = url.pathname.toLowerCase();
  const isMapsPath = path === "/maps" || path.startsWith("/maps/");
  if (host === "maps.app.goo.gl") return path.length > 1;
  if (host === "goo.gl") return isMapsPath;
  if (MAPS_GOOGLE_HOST.test(host)) return true;
  if (GOOGLE_HOST.test(host)) return isMapsPath;
  return false;
};

const COORDINATES_TEXT = /^\s*-?\d{1,2}(\.\d+)?\s*,\s*-?\d{1,3}(\.\d+)?\s*$/;

/** Plain coordinates written "lat, lng" (lat -90..90, lng -180..180, not both 0). Same rule as core-api. */
export const isCoordinatesText = (value: unknown): boolean => {
  if (typeof value !== "string" || !COORDINATES_TEXT.test(value)) return false;
  const [latitude, longitude] = value.split(",").map(part => Number(part.trim()));
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return false;
  return !(latitude === 0 && longitude === 0);
};

// local@domain.tld: no spaces, one @, a dot in the domain and a letters-only top-level part of 2+ (core-api's Joi
// email() also wants a real TLD, so "a@b.c" or "a@b" would be skipped there).
const EMAIL_PATTERN = /^[^\s@<>,;]+@[^\s@<>,;]+\.[a-z]{2,}$/i;

/** Whether the external mentor email answer is one core-api will copy. Blank is not a format error here: the required rule is separate. */
export const isMentorEmailInput = (value: unknown): boolean => {
  const text = typeof value === "string" ? value.trim() : "";
  return !text || (text.length <= EXTERNAL_MENTOR_EMAIL_MAX && EMAIL_PATTERN.test(text));
};

// core-api's rule for the copied mobile: digits only (CONSTANTS.USER.MOBILE_REGEX), 6 to 17 of them after trimming.
const PHONE_PATTERN = /^[0-9]{6,17}$/;

/** Whether the external mentor phone answer is one core-api will copy. Blank is fine: the question is optional. */
export const isMentorPhoneInput = (value: unknown): boolean => {
  const text = typeof value === "string" ? value.trim() : "";
  return !text || PHONE_PATTERN.test(text);
};

/** What the office question accepts: a Google Maps link or the office's coordinates. */
export const isOfficeLocationInput = (value: unknown): boolean => isGoogleMapsUrl(value) || isCoordinatesText(value);

const markerOf = (question: V2Question) => (question.metadata as { weekZeroField?: string } | null | undefined)?.weekZeroField;

export const findWeekZeroQuestion = (questions: V2Question[], marker: string): V2Question | undefined =>
  (questions || []).find(question => markerOf(question) === marker);

/**
 * Week 0 render order: the mode question first, the office location right after it, then every other question in
 * its given order. Without the mode marker (or for a form with neither) `questions` is returned as is.
 */
export const orderWeekZeroQuestions = (questions: V2Question[]): V2Question[] => {
  const mode = findWeekZeroQuestion(questions, WEEK_ZERO_MODE_MARKER);
  if (!mode) return questions;
  const office = findWeekZeroQuestion(questions, WEEK_ZERO_OFFICE_URL_MARKER);
  const rest = questions.filter(question => question !== mode && question !== office);
  return office ? [mode, office, ...rest] : [mode, ...rest];
};

/** `metadata.completionCertificateField` values on the Internship Completion Details form. */
export const COMPLETION_CERTIFICATE_FIELDS = ["certificate", "costToIndustry", "savingsToIndustry"];
/** Accepted file types for every completion certificate question and the Week 0 offer letter. */
export const INTERNSHIP_DOCUMENT_FILE_TYPES = ["pdf", "png", "doc", "docx"];
/** Kept so existing imports keep working. */
export const COMPLETION_CERTIFICATE_FILE_TYPES = INTERNSHIP_DOCUMENT_FILE_TYPES;

/** Whether the question carries a completion certificate marker. */
export const isCompletionCertificateQuestion = (question: V2Question): boolean => {
  const field = (question.metadata as { completionCertificateField?: string } | null | undefined)?.completionCertificateField;
  return typeof field === "string" && COMPLETION_CERTIFICATE_FIELDS.includes(field);
};

/**
 * Whether a FILE_UPLOAD question accepts the four internship document types (PDF, PNG, DOC, DOCX): a completion
 * certificate question or the Week 0 offer letter. Overrides the PDF-only `validation.fileTypes` saved on existing forms.
 */
export const isFourTypeUploadQuestion = (question: V2Question): boolean =>
  isCompletionCertificateQuestion(question) || markerOf(question) === WEEK_ZERO_OFFER_LETTER_MARKER;

/**
 * Always required for the learner, even on forms saved with it not required (the builder cannot change those): the
 * Week 0 offer letter and external mentor email, and every completion certificate question. Returns the question with
 * `isRequired: true` when it carries one of those markers; any other question is returned as is.
 */
export const applyWeekZeroRequired = (question: V2Question): V2Question => {
  const marker = markerOf(question);
  const forced = marker === WEEK_ZERO_OFFER_LETTER_MARKER || marker === WEEK_ZERO_MENTOR_EMAIL_MARKER || isCompletionCertificateQuestion(question);
  return forced && !question.isRequired ? { ...question, isRequired: true } : question;
};

/** The stored value (option.value, what a pick saves) of the mode's "Offline" option, matched by value or label (case-insensitive, trimmed); undefined if none. */
export const defaultWeekZeroModeValue = (questions: V2Question[]): { questionId: string; value: string } | undefined => {
  const mode = findWeekZeroQuestion(questions, WEEK_ZERO_MODE_MARKER);
  const option = (mode?.options || []).find(
    item => (item.value || "").trim().toLowerCase() === "offline" || (item.label || "").trim().toLowerCase() === "offline"
  );
  return mode && option ? { questionId: mode.id, value: option.value } : undefined;
};

/** True when the mode answer is Offline or Hybrid (case-insensitive, trimmed). */
export const isOfficeMode = (modeAnswer: unknown): boolean => {
  if (typeof modeAnswer !== "string") return false;
  const mode = modeAnswer.trim().toLowerCase();
  return mode === "offline" || mode === "hybrid";
};

/** Antd Form.Item rules for the office location while it is shown: required, then the link-or-coordinates rule. */
export const officeLocationRules = () => [
  { required: true, whitespace: true, message: "This field is required" },
  {
    validator: (_: unknown, value: unknown) =>
      typeof value !== "string" || !value.trim() || isOfficeLocationInput(value) ? Promise.resolve() : Promise.reject(new Error(GOOGLE_MAPS_URL_MESSAGE))
  }
];

/**
 * The questions to submit / save for the current values: the office link is dropped while the mode is not
 * Offline / Hybrid, so a stale link is never sent. Without the markers this returns `questions` as is.
 */
export const questionsForSubmission = (questions: V2Question[], values: Record<string, unknown>): V2Question[] => {
  const mode = findWeekZeroQuestion(questions, WEEK_ZERO_MODE_MARKER);
  const office = findWeekZeroQuestion(questions, WEEK_ZERO_OFFICE_URL_MARKER);
  if (!mode || !office || isOfficeMode(values?.[mode.id])) return questions;
  return questions.filter(question => question.id !== office.id);
};

/** Trims the office link answer (only that one) before it is sent. */
export const trimOfficeLocationAnswer = (questions: V2Question[], responses: V2Response[]): V2Response[] => {
  const office = findWeekZeroQuestion(questions, WEEK_ZERO_OFFICE_URL_MARKER);
  if (!office) return responses;
  return responses.map(response =>
    response.questionId === office.id && typeof response.answer === "string" ? { ...response, answer: response.answer.trim() || null } : response
  );
};
